import React, { useState, useEffect, useCallback } from 'react';
import { Form, Button, Card, message } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import DataSetQueryConditionModal from '../modals/DataSetQueryConditionModal';
import DataSetQueryConditionEditModal from '../modals/DataSetQueryConditionEditModal';

/**
 * 可重用的 DataSet 查詢條件群組編輯器（與 dataSetQuery 節點 UI 一致）。
 * @param {Array} conditionGroups
 * @param {(groups: object[]) => void} onConditionGroupsChange
 * @param {function} generateWhereClause
 * @param {function} t
 * @param {Array} processVariables
 * @param {Array} dataSetColumns
 * @param {function} loadDataSetColumns — (dataSetId) => void
 * @param {string} [dataSetId] — 選定 Dataset 時載入欄位供條件編輯
 */
const DataSetQueryConditionGroupsEditor = ({
  conditionGroups = [],
  onConditionGroupsChange,
  generateWhereClause,
  t,
  processVariables,
  dataSetColumns,
  loadDataSetColumns,
  dataSetId
}) => {
  const [dataSetConditionGroupModalVisible, setDataSetConditionGroupModalVisible] = useState(false);
  const [dataSetConditionEditModalVisible, setDataSetConditionEditModalVisible] = useState(false);
  const [editingDataSetConditionGroup, setEditingDataSetConditionGroup] = useState(null);
  const [editingDataSetCondition, setEditingDataSetCondition] = useState(null);
  const [dataSetConditionGroupForm] = Form.useForm();
  const [dataSetConditionForm] = Form.useForm();

  useEffect(() => {
    if (dataSetId && loadDataSetColumns) {
      loadDataSetColumns(dataSetId);
    }
  }, [dataSetId, loadDataSetColumns]);

  const handleSaveDataSetConditionGroup = async (values) => {
    try {
      const currentGroups = conditionGroups || [];

      if (editingDataSetConditionGroup.groupIndex === -1) {
        const newGroup = {
          id: `conditionGroup${Date.now()}`,
          relation: values.relation,
          conditions: editingDataSetConditionGroup.conditions || []
        };
        onConditionGroupsChange([...currentGroups, newGroup]);
      } else {
        const newGroups = [...currentGroups];
        newGroups[editingDataSetConditionGroup.groupIndex] = {
          ...newGroups[editingDataSetConditionGroup.groupIndex],
          relation: values.relation,
          conditions: editingDataSetConditionGroup.conditions || []
        };
        onConditionGroupsChange(newGroups);
      }

      setDataSetConditionGroupModalVisible(false);
      setEditingDataSetConditionGroup(null);
      dataSetConditionGroupForm.resetFields();
      message.success(t('workflowDesigner.dataSet.queryConditionGroupSaved'));
    } catch (error) {
      console.error(t('workflowDesigner.dataSet.queryConditionGroupSaveFailed') + ':', error);
      message.error(t('workflowDesigner.dataSet.queryConditionGroupSaveFailed'));
    }
  };

  const handleEditDataSetCondition = (condition, condIndex) => {
    setEditingDataSetCondition({ ...condition, condIndex });
    setDataSetConditionEditModalVisible(true);
    const isVariableRef = /^\$\{[^}]+\}$/.test(condition.value || '');
    const savedValueSource = condition.valueSource;
    const resolvedValueSource =
      savedValueSource === 'userNaturalLanguage' ? 'userNaturalLanguage'
      : isVariableRef ? 'variable'
      : 'fixed';
    dataSetConditionForm.setFieldsValue({
      fieldName: condition.fieldName,
      operator: condition.operator,
      valueSource: resolvedValueSource,
      value: condition.value ?? '',
      valueJsonKey: condition.valueJsonKey ?? undefined,
      nlFallback: condition.nlFallback ?? 'keepAll',
      nlMinFieldLength: condition.nlMinFieldLength ?? 2,
      label: condition.label
    });
  };

  const handleAddDataSetCondition = () => {
    setEditingDataSetCondition({ condIndex: -1 });
    setDataSetConditionEditModalVisible(true);
    dataSetConditionForm.resetFields();
    dataSetConditionForm.setFieldsValue({ valueSource: 'fixed' });
  };

  const handleDeleteDataSetCondition = (condIndex) => {
    const newConditions = [...(editingDataSetConditionGroup?.conditions || [])];
    newConditions.splice(condIndex, 1);
    setEditingDataSetConditionGroup({
      ...editingDataSetConditionGroup,
      conditions: newConditions
    });
  };

  const handleSaveDataSetCondition = async (values) => {
    try {
      const isNlMode = values.valueSource === 'userNaturalLanguage';
      const newCondition = {
        id: editingDataSetCondition?.id || `condition${Date.now()}`,
        fieldName: values.fieldName,
        operator: isNlMode ? '' : (values.operator ?? ''),
        value: isNlMode ? '' : (values.value ?? ''),
        valueSource: values.valueSource ?? 'fixed',
        valueJsonKey: (values.valueSource === 'variable' && values.valueJsonKey) ? values.valueJsonKey : null,
        nlFallback: isNlMode ? (values.nlFallback ?? 'keepAll') : 'keepAll',
        nlMinFieldLength: isNlMode ? (values.nlMinFieldLength ?? 2) : 2,
        label: values.label
      };

      const newConditions = [...(editingDataSetConditionGroup?.conditions || [])];

      if (editingDataSetCondition.condIndex === -1) {
        newConditions.push(newCondition);
      } else {
        newConditions[editingDataSetCondition.condIndex] = newCondition;
      }

      setEditingDataSetConditionGroup({
        ...editingDataSetConditionGroup,
        conditions: newConditions
      });

      setDataSetConditionEditModalVisible(false);
      setEditingDataSetCondition(null);
      dataSetConditionForm.resetFields();
      message.success(t('workflowDesigner.dataSet.queryConditionSaved'));
    } catch (error) {
      console.error(t('workflowDesigner.dataSet.queryConditionSaveFailed') + ':', error);
      message.error(t('workflowDesigner.dataSet.queryConditionSaveFailed'));
    }
  };

  const openAddGroupModal = useCallback(() => {
    const newConditionGroup = {
      id: `conditionGroup${Date.now()}`,
      relation: 'and',
      conditions: [],
      groupIndex: -1
    };
    setEditingDataSetConditionGroup(newConditionGroup);
    setDataSetConditionGroupModalVisible(true);
  }, []);

  const currentConditionGroups = conditionGroups || [];

  return (
    <>
      <div style={{ marginBottom: '8px' }}>
        <Button
          type="dashed"
          onClick={openAddGroupModal}
          style={{ width: '100%' }}
        >
          {t('workflowDesigner.dataSet.addQueryConditionGroup')}
        </Button>
      </div>
      {currentConditionGroups.map((group, groupIndex) => (
        <Card
          key={group.id}
          size="small"
          style={{
            marginBottom: '8px',
            border: '1px solid #d9d9d9',
            cursor: 'pointer'
          }}
          onClick={() => {
            const editingGroup = { ...group, groupIndex };
            setEditingDataSetConditionGroup(editingGroup);
            setDataSetConditionGroupModalVisible(true);
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                {t('workflowDesigner.dataSet.queryConditionGroup')} {groupIndex + 1}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                {t('workflowDesigner.dataSet.conditionCount')}: {group.conditions?.length || 0}
                {group.relation && ` • ${group.relation.toUpperCase()}`}
              </div>
              {group.conditions && group.conditions.length > 0 && (
                <div style={{ fontSize: '11px', color: '#999' }}>
                  {group.conditions.slice(0, 2).map(condition =>
                    `${condition.fieldName} ${condition.operator} ${condition.value}`
                  ).join(', ')}
                  {group.conditions.length > 2 && ` +${group.conditions.length - 2} more`}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  const editingGroup = { ...group, groupIndex };
                  setEditingDataSetConditionGroup(editingGroup);
                  setDataSetConditionGroupModalVisible(true);
                }}
              />
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  const newGroups = currentConditionGroups.filter((_, i) => i !== groupIndex);
                  onConditionGroupsChange(newGroups);
                }}
              />
            </div>
          </div>
        </Card>
      ))}

      {currentConditionGroups.length > 0 && (
        <div style={{
          marginTop: '12px',
          padding: '8px 12px',
          backgroundColor: '#f5f5f5',
          borderRadius: '4px',
          fontSize: '12px',
          color: '#666'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{t('workflowDesigner.dataSet.generatedWhereClause')}:</div>
          <div style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {generateWhereClause(currentConditionGroups)}
          </div>
        </div>
      )}

      <DataSetQueryConditionModal
        visible={dataSetConditionGroupModalVisible}
        onCancel={() => {
          setDataSetConditionGroupModalVisible(false);
          setEditingDataSetConditionGroup(null);
          dataSetConditionGroupForm.resetFields();
        }}
        editingConditionGroup={editingDataSetConditionGroup}
        conditionGroupForm={dataSetConditionGroupForm}
        dataSetColumns={dataSetColumns}
        processVariables={processVariables}
        onSave={handleSaveDataSetConditionGroup}
        onEditCondition={handleEditDataSetCondition}
        onAddCondition={handleAddDataSetCondition}
        onDeleteCondition={handleDeleteDataSetCondition}
        t={t}
      />

      <DataSetQueryConditionEditModal
        visible={dataSetConditionEditModalVisible}
        onCancel={() => {
          setDataSetConditionEditModalVisible(false);
          setEditingDataSetCondition(null);
          dataSetConditionForm.resetFields();
        }}
        editingCondition={editingDataSetCondition}
        conditionForm={dataSetConditionForm}
        dataSetColumns={dataSetColumns}
        processVariables={processVariables}
        onSave={handleSaveDataSetCondition}
        t={t}
      />
    </>
  );
};

export default DataSetQueryConditionGroupsEditor;
